import React, { lazy } from 'react';
import { RouteComponentProps } from '@reach/router';
import { endpoints } from '../../endpoints.config';
import Category from '../../components/Category';
import { CrudItems } from 'components/Category/CrudMenu';
import { localizedFormatDate } from '../../helpers/categoryHelpers/userLocale';
import { useTranslation } from 'react-i18next';

// Icons
import View from '../../assets/Cat-Entity/Icon_Action_01.png';
import Update from '../../assets/Cat-Entity/Icon_Action_02.png';

const Approval = lazy(() => import('./ManageApproval'));

// Define the url for the table's data endpoint.
const url = endpoints.approvals.list;

// Define which column on the table represents each row's ID.
const idColumn: keyof ApprovalsListData = 'approvals__Id';

//Define the data structure for table
export type ApprovalsListData = {
    approvals__Id: number;
    approvals__Code: string;
    approvals__Type: string;
    approvals__Description: string;
    approvals__AddedBy: string;
    approvals__AddDate: string;
    approvals__ApprovalsRequired: number;
    approvals__ApprovalsReceived: number;
};

const getCrudItems = (t: (key: string, options?: any) => any): CrudItems<ApprovalsListData> => [
    {
        icon: View,
        title: t('viewApproval'),
        imgAltText: t('view', { ns: 'common' }),
        menuCode: 'VIEW',
        showInMultiSelect: false,
        content: (onDone, selectedData, menu) => (
            <Approval
                onDone={onDone}
                selectedData={selectedData}
                idFromRowOption={menu.selectedId}
                actionType="view"
                submitButtonText={t('close', { ns: 'common' })}
                noSubmit={true}
            />
        ),
        inRightPanel: true,
        isDisabled: false,
    },
    {
        icon: Update,
        imgAltText: t('update', { ns: 'common' }),
        title: t('approveReject'),
        menuCode: 'UPDATE',
        showInMultiSelect: false,
        content: (onDone, selectedData, menu) => (
            <Approval
                selectedData={selectedData}
                onDone={onDone}
                idFromRowOption={menu.selectedId}
                actionType="edit"
                submitButtonText={t('update', { ns: 'common' })}
            />
        ),
        inRightPanel: true,
        isDisabled: false,
    },
];

const approvalNameKeys: Record<string, string> = {
    'reverse inbound external transfers': 'reverseExternalTransfers',
    'reverse incoming external transfers': 'reverseExternalTransfers',
    'pull ach': 'achPull',
    'ach pull': 'achPull',
    'create payee': 'createBeneficiary',
    'create beneficiary': 'createBeneficiary',
    'create external transfer': 'createExternalTransfer',
    'cancel customer transaction': 'cancelClientTransaction',
    'cancel client transaction': 'cancelClientTransaction',
    'asset exchange': 'assetExchange',
    'enable cryptocurrencies for dda accounts': 'enableCryptoForDda',
};

// Optional - function for formatting a row by column name. Takes a row and should
// spit out an object with the same keys.
// Should do this for date columns and format them as required
const rowFormatter = (row: ApprovalsListData) => {
    let formattedRow: { [K in keyof typeof row]: React.ReactNode } = { ...row };
    if (formattedRow.approvals__AddDate) {
        formattedRow = {
            ...formattedRow,
            approvals__AddDate: localizedFormatDate(new Date(row.approvals__AddDate)),
        };
    }
    if (row.approvals__ApprovalsReceived != null && row.approvals__ApprovalsRequired != null) {
        formattedRow.approvals__ApprovalsReceived = `${row.approvals__ApprovalsReceived}/${row.approvals__ApprovalsRequired}`;
    }
    return formattedRow;
};

type Props = RouteComponentProps;

function ManageApprovals(props: Props) {
    const { t } = useTranslation('approvals');
    return (
        <Category<ApprovalsListData>
            location={props.location}
            contentTitle={t('approvals')}
            noun={t('approvals')}
            idColumn={idColumn}
            listEndpointUrl={url}
            rowFormatter={rowFormatter}
            primitiveFormatters={{
                string: (s: string) => {
                    const key = approvalNameKeys[s.toLowerCase()];
                    return <span>{key ? t(key) : s}</span>;
                },
            }}
            crudComponents={getCrudItems(t)}
            hiddenColumns={[idColumn]}
            rowCellOptions={{
                approvals__Description: {
                    width: 'Large',
                    style: { flex: 4 },
                },
            }}
            noAddBtn
            classname="listsCatTable"
            emptyTableLabel={t('noApprovalsFound', { ns: 'common' })}
        />
    );
}

export default ManageApprovals;
